/**
 * IndicePanel — GEN-EKB-014: salud del índice de búsqueda (solo admin). GEN-PX-017: vive en Admin › Salud (antes, al pie de KBs).
 * Dice si el índice está al día (iKus vigentes vs proyectados, proyecciones de otra versión o
 * de otro modelo) y ofrece «Reconstruir índice», que lo regenera desde los .md vigentes.
 * Buscar es de E03: aquí solo se mira y se mantiene el índice. El kernel revalida que es admin.
 * GEN-EKB-016: cuenta los .md huérfanos (en el almacén sin fila en la base) y ofrece quitarlos
 * con su propio botón, aparte de «Reconstruir índice» (que necesita embeddings; el barrido no).
 */

import { useCallback, useEffect, useState } from 'react'
import { barrerHuerfanos, getSaludIndice, reconstruirIndice } from '../api/client.js'

const MENSAJES = {
  embeddings_no_disponible: 'El servicio de embeddings no está configurado en esta instalación.',
  ekb_no_disponible: 'La base de conocimiento aún no está disponible (o falta aplicar el esquema del índice).',
  contenido_no_disponible: 'El almacén de contenido no está disponible.',
  huerfanos_no_disponible: 'No se pudo recorrer el almacén de contenido (¿faltan permisos?): no se ha quitado nada.',
  forbidden: 'Solo el administrador ve y reconstruye el índice.',
}
const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'

function modeloTexto(m) {
  return `${m.modelo}${m.modelo_version ? `:${m.modelo_version}` : ''} · ${m.dimension}`
}

export default function IndicePanel({ onReconstruido }) {
  const [salud, setSalud] = useState(null)
  const [error, setError] = useState(null)
  const [nota, setNota] = useState(null)
  const [trabajando, setTrabajando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      setSalud((await getSaludIndice()).indice)
      setError(null)
    } catch (err) {
      setError(mensaje(err))
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  async function reconstruir() {
    if (!window.confirm('¿Reconstruir el índice de búsqueda desde los .md de los iKus vigentes?')) return
    setTrabajando(true)
    setNota(null)
    setError(null)
    try {
      const r = await reconstruirIndice()
      setSalud(r.indice)
      const x = r.reconstruccion
      setNota(`Índice reconstruido: ${x.proyectados} de ${x.vigentes} iKus vigentes proyectados` +
        (x.fallidos ? `, ${x.fallidos} con fallo` : '') + (x.quitados ? `, ${x.quitados} proyecciones sobrantes quitadas` : '') + '.')
      onReconstruido?.()
    } catch (err) {
      setError(mensaje(err))
    } finally {
      setTrabajando(false)
    }
  }

  async function barrer() {
    if (!window.confirm('¿Quitar los .md del almacén que no tienen iKu en la base? No se puede deshacer.')) return
    setTrabajando(true)
    setNota(null)
    setError(null)
    try {
      const r = await barrerHuerfanos()
      setSalud(r.indice)
      const b = r.barrido
      setNota(`.md huérfanos quitados: ${b.quitados}` + (b.fallidos ? `, ${b.fallidos} no se pudieron quitar` : '') +
        (b.recientes_conservados ? `, ${b.recientes_conservados} recientes conservados (menos de una hora)` : '') + '.')
      onReconstruido?.()
    } catch (err) {
      setError(mensaje(err))
    } finally {
      setTrabajando(false)
    }
  }

  const h = salud?.huerfanos

  return (
    <section className="indice-salud" aria-label="Índice de búsqueda">
      <h3>Índice de búsqueda</h3>
      {!salud && !error && <p className="equipo-estado">Cargando…</p>}
      {salud && (
        <>
          <p className="equipo-estado">
            <strong>{salud.al_dia ? 'Al día' : 'No está al día'}</strong>
            {' · '}{salud.proyectados} de {salud.vigentes} iKus vigentes proyectados
            {salud.sin_proyectar > 0 && ` · ${salud.sin_proyectar} pendientes de proyectar`}
            {salud.desfasados > 0 && ` · ${salud.desfasados} con una versión distinta de la vigente`}
            {salud.otro_modelo > 0 && ` · ${salud.otro_modelo} con otro modelo`}
            {salud.sobrantes > 0 && ` · ${salud.sobrantes} proyecciones de iKus no vigentes`}
          </p>
          <p className="kbs-pendiente">
            Modelo vigente: {modeloTexto(salud.modelo_vigente)}
            {!salud.embeddings_disponible && ' (servicio de embeddings no configurado)'}
            {salud.modelos?.length > 0 && ` · en el índice: ${salud.modelos.map(m => `${modeloTexto(m)} (${m.ikus})`).join(', ')}`}
            {salud.ultima_reconstruccion && ` · última reconstrucción: ${new Date(salud.ultima_reconstruccion.ts).toLocaleString()} por ${salud.ultima_reconstruccion.actor}`}
          </p>
          {h && (
            <p className="kbs-pendiente" data-testid="huerfanos">
              {!h.disponible
                ? '.md huérfanos: no disponible (no se puede recorrer el almacén de contenido)'
                : `.md huérfanos (sin iKu en la base): ${h.md}` +
                  (h.md ? ` de ${h.ikus} iKus` : '') +
                  (h.recientes ? ` · ${h.recientes} recientes (menos de una hora, no se quitan aún)` : '') +
                  (h.no_reconocidos ? ` · ${h.no_reconocidos} ficheros no reconocidos (no se tocan)` : '')}
            </p>
          )}
        </>
      )}
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      {nota && <p className="equipo-estado" role="status">{nota}</p>}
      <button type="button" className="btn btn--ghost" onClick={reconstruir} disabled={trabajando}>
        {trabajando ? 'Trabajando…' : 'Reconstruir índice'}
      </button>
      {h?.disponible && h.md > 0 && (
        <button type="button" className="btn btn--ghost" onClick={barrer} disabled={trabajando}>
          Quitar .md huérfanos
        </button>
      )}
    </section>
  )
}
